import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { adminFrameworksApi } from '../services/api';
import { PageHeader } from '../components/PageHeader';
import { LinkedArtefacts } from '../components/LinkedArtefacts';

// Read-only v1 - editing a framework's own definition (name/category/version/controls) stays on
// AdminFrameworksPage, which already owns that flow. This page exists purely so a framework has
// somewhere for its Linked Artefacts (e.g. Evidence linked to it) to actually be browsable from -
// before this page, frameworks had no individual route at all, only the flat admin list.
export const FrameworkDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [activeTab, setActiveTab] = useState<'details' | 'artefacts'>('details');

  const { data: framework, isLoading } = useQuery({
    queryKey: ['framework', id],
    queryFn: () => adminFrameworksApi.getOne(id!),
    enabled: !!id,
  });

  if (isLoading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <div className="d-flex justify-content-center py-5">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Loading…</span>
          </div>
        </div>
      </div>
    );
  }

  if (!framework) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y np-page">
        <PageHeader icon="ri-shield-star-line" title="Framework not found" colour="secondary" />
      </div>
    );
  }

  return (
    <div className="container-xxl flex-grow-1 container-p-y np-page">
      <PageHeader icon="ri-shield-star-line" title={framework.name || id} colour="dark" />

      <div className="mb-3 d-flex align-items-center gap-2 flex-wrap">
        {framework.code && <span className="np-chip np-tone-dark np-tabular">{framework.code}</span>}
        {framework.category && <span className="np-chip np-tone-neutral">{framework.category}</span>}
        {framework.version && <span className="np-sub">v{framework.version}</span>}
      </div>

      <div className="np-tabs" style={{ overflowX: 'auto', flexWrap: 'nowrap' }}>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'details' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('details')}
        >
          <i className="ri-file-list-3-line"></i>Details
        </button>
        <button
          type="button"
          className={`np-tab d-flex align-items-center gap-1${activeTab === 'artefacts' ? ' active' : ''}`}
          style={{ whiteSpace: 'nowrap' }}
          onClick={() => setActiveTab('artefacts')}
        >
          <i className="ri-links-line"></i>Linked Artefacts
        </button>
      </div>

      {activeTab === 'details' && (
        <div className="np-card mt-3">
          <div className="np-card-head">
            <h2>Framework Details</h2>
          </div>
          <div className="np-card-body">
            {framework.description ? (
              <p className="np-sub mb-0">{framework.description}</p>
            ) : (
              <p className="np-sub mb-0">No description recorded.</p>
            )}
            <small className="np-hint d-block mt-3">
              To edit this framework's own definition (name, category, version, controls),
              use Admin → Frameworks.
            </small>
          </div>
        </div>
      )}

      {activeTab === 'artefacts' && (
        <div className="row mt-3">
          <LinkedArtefacts artefactId={id!} artefactType="framework" artefactRegister="frameworks" />
        </div>
      )}
    </div>
  );
};
